import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, ShieldQuestion } from 'lucide-react';
import { submitReviewRequestThunk } from '../../redux/contentReview/contentReviewActionThunks';
import { clearSubmitError } from '../../redux/contentReview/contentReviewSlice';

/**
 * The user-facing side of the GDPR Art 22 / DSA Art 14(1)/17 dispute path:
 * request a human review of a moderation decision or a resolved prediction
 * outcome. Triggered from a notification's "Request Review" action.
 */
const RequestReviewModal = ({ targetType, targetId, contextLabel, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, errors } = useSelector((state) => state.contentReview);
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    if (reason.trim().length < 10) return;
    dispatch(clearSubmitError());
    try {
      await dispatch(
        submitReviewRequestThunk({ target_type: targetType, target_id: targetId, reason: reason.trim() })
      ).unwrap();
      onSubmitted?.();
      onClose();
    } catch {
      // errors.submit already surfaced via redux state
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <ShieldQuestion className="w-5 h-5 text-blue-300" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{t('requestReviewModal.title')}</h3>
              {contextLabel && <p className="text-xs text-slate-400 mt-0.5">{contextLabel}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-400">
            {t('requestReviewModal.intro')}
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('requestReviewModal.placeholder')}
            rows={5}
            maxLength={2000}
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-white/10 text-slate-100 placeholder-slate-500 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{t('requestReviewModal.charCount', { count: reason.trim().length, max: 2000, min: 10 })}</span>
          </div>
          {errors.submit && <p className="text-sm text-red-400">{errors.submit}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/5">
            {t('requestReviewModal.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={reason.trim().length < 10 || loading.submit}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
          >
            {loading.submit ? t('requestReviewModal.submitting') : t('requestReviewModal.submitRequest')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestReviewModal;
